import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { TFunction } from 'i18next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Location, useLocation } from 'react-router-dom'

import { getErrorResponseData } from '@app/routing/Session/ErrorResponse'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useAccountInfo } from '@context/AccountInfoContext/AccountInfoContext.Provider'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Operator, ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { wait } from '@utils/wait'

import { AccountGroupType } from '@model/AccountGroupType'
import { ClientCampaignDto } from '@model/ClientCampaignDto'
import { CreateCampaignTradingAccount, CreateTradingAccount } from '@model/CreateTradingAccount'
import { IntroducingBrokerValidateCode } from '@model/IntroducingBrokerValidateCode'
import { NameDto } from '@model/NameDto'
import {
  PlatformTypeEnum,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'
import { TickmillProductType } from '@model/TickmillProductType'
import { TradingAccount } from '@model/TradingAccount'
import { WalletCurrency, WalletTypeEnum } from '@model/WalletDto'

import { scrollToTop, useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'

import { CampaignExtendedNameDto } from './CreateTradingAccountCampaign.types'
import { CreateTradingAccountCampaignForm } from './CreateTradingAccountCampaignForm'

interface CampaignExtendedName {
  id: string
  name: string
  platformTypes: NameDto[]
  campaignDescription?: string
}

type SubmitValues = CreateTradingAccount | CreateCampaignTradingAccount

const TRADING_ACCOUNT_REFRESH_RETRY_COUNT = 4
const TRADING_ACCOUNT_REFRESH_RETRY_DELAY_MS = 1000

const getCommonCurrencies = (
  platformCurrencies: WalletCurrency[],
  walletCurrencies: WalletCurrency[]
): WalletCurrency[] => {
  return walletCurrencies.filter((walletCurrency) =>
    platformCurrencies.some((platformCurrency) => platformCurrency.id === walletCurrency.id)
  )
}

const getNewestTradingAccount = (accounts: TradingAccount[] = []): TradingAccount | undefined =>
  [...accounts].sort(
    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
  )[0]

const findNewRegularTradingAccount = (
  accounts: TradingAccount[] = [],
  previousRegularAccountIds: string[] = []
): TradingAccount | undefined => {
  const previousIds = new Set(previousRegularAccountIds)
  return getNewestTradingAccount(accounts.filter((account) => !previousIds.has(account.id)))
}

const useCreateTradingAccountCampaignMutate = (
  onSuccess: (values: SubmitValues) => Promise<void> | void
) => {
  const { t } = useTranslation()
  const { refreshLimits } = useAccountInfo()
  const { product } = useProductReadContext()

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { addSnackbar } = useSnackbar()

  const [isCurrenciesLoading, setIsCurrenciesLoading] = useState(false)
  const [currencies, setCurrencies] = useState<WalletCurrency[]>([])

  const handleSubmit = async (values: SubmitValues) => {
    try {
      if (values.campaignId) {
        await apiClient.addCampaignTradingAccount(values as CreateCampaignTradingAccount)
      } else {
        await apiClient.addTradingAccount(values as CreateTradingAccount)
      }

      queryClient.removeQueries({ queryKey: ['tradingAccount'] })

      scrollToTop()
      await onSuccess(values)
    } catch (error: unknown) {
      const { errorCode, errorMessage } = getErrorResponseData(error as ResponseError)
      if (error instanceof ResponseError && errorCode === 'trading_account_opening_restriction') {
        error.preventDefault()
        addSnackbar.error({
          message: t('errors.Wallet has trading account opening restriction'),
        })
        await queryClient.invalidateQueries({ queryKey: queryKeys.wallet.list() })
        return
      }

      const message = errorMessage ?? t('errors.Something went wrong! Please try again later')

      addSnackbar.error({ message })
    } finally {
      await refreshLimits()
    }
  }

  const handleChangePlatform = useCallback(
    async (platformTypeId: number, introducingBrokerId?: string) => {
      setIsCurrenciesLoading(true)
      try {
        const walletTypeId =
          product === TickmillProductType.ETD ? WalletTypeEnum.ETD : WalletTypeEnum.CFD
        const [platformCurrencies, walletCurrencies] = await Promise.all([
          introducingBrokerId
            ? apiClient.getIntroducingBrokerCurrenciesByPlatformId(
                introducingBrokerId,
                platformTypeId
              )
            : apiClient.getCurrencies(platformTypeId),
          apiClient.getWalletCurrencies(walletTypeId),
        ])
        setCurrencies(getCommonCurrencies(platformCurrencies, walletCurrencies))
      } finally {
        setIsCurrenciesLoading(false)
      }
    },
    [apiClient, product]
  )

  return {
    isCurrenciesLoading,
    currencies,
    handleSubmit,
    handleChangePlatform,
  }
}

export const useMainIBValidation = (
  ibCode: string | undefined
): {
  ibValidation: IntroducingBrokerValidateCode | null
  isLoading: boolean
} => {
  const apiClient = useApiClient(ClientApiClient)

  const { data: ibValidation, isLoading } = useQuery({
    queryKey: queryKeys.tradingAccount.ibValidation(ibCode),
    queryFn: () => apiClient.validateAccountIntroducingBrokerByCode(ibCode!),
    enabled: !!ibCode,
  })

  return {
    ibValidation: ibValidation ?? null,
    isLoading,
  }
}

const useCreateTradingAccountCampaignFetch = (refreshToken: number) => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const [hasTMTOrTVAccount, setHasTMTOrTVAccount] = useState(false)

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: queryKeys.campaign.client(locale),
    queryFn: () => apiClient.getClientCampaigns(locale),
  })

  const [campaignAccount, setCampaignAccount] = useState<TradingAccount | undefined>()
  const [isTradingAccountLoading, setIsTradingAccountLoading] = useState(false)
  const [hasAnyLiveTA, setHasAnyLiveTA] = useState(false)
  const [regularAccountIds, setRegularAccountIds] = useState<string[]>([])

  const refreshTradingAccounts = useCallback(async () => {
    setIsTradingAccountLoading(true)
    try {
      const tradingAccounts = await apiClient.getTradingAccounts({
        search: {
          AccountGroupType: {
            value: AccountGroupType.Live,
            operator: Operator.EQUAL,
          },
          Platform: {
            value: PlatformTypeEnum.CQGCAST,
            operator: Operator.NOT_EQUAL,
          },
        },
      })

      if (tradingAccounts.items.length) {
        const regularAccounts = tradingAccounts.items.filter((x) => !x.campaigns.length)

        setHasAnyLiveTA(true)
        setRegularAccountIds(regularAccounts.map((x) => x.id))
        const tmtOrTVExists = tradingAccounts.items.some(
          (a) =>
            a.platformType.id === PlatformTypeEnum.TickmillTrader ||
            a.platformType.id === PlatformTypeEnum.TradingView
        )
        setHasTMTOrTVAccount(tmtOrTVExists)
        const campaignTA = tradingAccounts.items.find((x) => !!x.campaigns.length)
        setCampaignAccount(campaignTA)
        const newestRegularTA = getNewestTradingAccount(regularAccounts)
        return { campaignTA, newestRegularTA, regularAccounts }
      }

      setHasAnyLiveTA(false)
      setRegularAccountIds([])
      setCampaignAccount(undefined)
      return { campaignTA: undefined, newestRegularTA: undefined, regularAccounts: [] }
    } finally {
      setIsTradingAccountLoading(false)
    }
  }, [apiClient])

  useEffect(() => {
    refreshTradingAccounts()
  }, [refreshTradingAccounts, refreshToken])

  return {
    campaigns,
    isLoading,
    campaignAccount,
    hasAnyLiveTA,
    isTradingAccountLoading,
    refreshTradingAccounts,
    hasTMTOrTVAccount,
    regularAccountIds,
  }
}

type LocationState = Location<{
  platformType: NameDto
  previousValues?: PreviousValues
  returnPath?: string
}>

interface PreviousValues {
  tradingAccount: TradingAccount
}

export const CreateTradingAccountCampaignPage: React.FC = () => {
  useScrollToTop()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location: LocationState = useLocation()

  const { account } = useAccountReadContext()
  const locale = useSessionLanguage()

  const [selectedPlatform, setSelectedPlatform] = useState<NameDto | undefined>(
    location.state?.platformType
  )
  const [initialPlatformType] = useState<NameDto | undefined>(location.state?.platformType)
  const [tradingAccountsRefreshToken, setTradingAccountsRefreshToken] = useState(0)

  const urlParams = new URLSearchParams(location.search)
  const selectedCampaignIdFromQuery = urlParams.get('campaignId') ?? undefined
  const hasCampaignQuery = !!urlParams.get('campaign') || !!selectedCampaignIdFromQuery
  const hasBonusCampaignQuery = !!urlParams.get('bonusCampaign')

  const { ibValidation, isLoading: ibValidationLoading } = useMainIBValidation(
    account?.introducingBroker?.name
  )

  const {
    campaigns,
    isLoading,
    hasAnyLiveTA,
    isTradingAccountLoading,
    refreshTradingAccounts,
    hasTMTOrTVAccount,
    regularAccountIds,
  } = useCreateTradingAccountCampaignFetch(tradingAccountsRefreshToken)

  const findNewTAWithRetry = useCallback(
    async (
      previousRegularAccountIds: string[],
      attemptsLeft = TRADING_ACCOUNT_REFRESH_RETRY_COUNT
    ): Promise<TradingAccount | undefined> => {
      const refreshed = await refreshTradingAccounts().catch(() => undefined)
      const found = findNewRegularTradingAccount(
        refreshed?.regularAccounts,
        previousRegularAccountIds
      )
      if (found) return found

      if (attemptsLeft <= 0) return undefined

      await wait(TRADING_ACCOUNT_REFRESH_RETRY_DELAY_MS)
      return findNewTAWithRetry(previousRegularAccountIds, attemptsLeft - 1)
    },
    [refreshTradingAccounts]
  )

  const handleCreateSuccess = useCallback(
    async (values: SubmitValues) => {
      const isCampaign = !!values.campaignId
      const platformId = values.platformTypeId

      if (!isCampaign && (isTradingViewPlatformType(platformId) || isTMTPlatformType(platformId))) {
        navigate('success', { state: { canAccessSuccessPage: true } })
        return
      }

      if (!isCampaign) {
        await findNewTAWithRetry(regularAccountIds)
      }

      if (isCampaign) {
        setTradingAccountsRefreshToken((token) => token + 1)
      }

      navigate('success', { state: { canAccessSuccessPage: true } })
    },
    [regularAccountIds, findNewTAWithRetry, navigate, setTradingAccountsRefreshToken]
  )

  const { isCurrenciesLoading, currencies, handleSubmit, handleChangePlatform } =
    useCreateTradingAccountCampaignMutate(handleCreateSuccess)

  const transformData = useMemo(() => {
    return transformCampaigns(campaigns, t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, locale])

  return (
    <Loading isLoading={isLoading || ibValidationLoading || isTradingAccountLoading}>
      <CreateTradingAccountCampaignForm
        t={t}
        data={transformData}
        currencies={currencies}
        account={account}
        hasAnyLiveTA={hasAnyLiveTA}
        hasBonusCampaignQuery={hasBonusCampaignQuery}
        hasCampaignQuery={hasCampaignQuery}
        onChangePlatform={handleChangePlatform}
        isCurrenciesLoading={isCurrenciesLoading}
        onSubmit={handleSubmit}
        setSelectedPlatform={setSelectedPlatform}
        initialPlatformType={initialPlatformType}
        selectedPlatform={selectedPlatform}
        selectedCampaignId={
          selectedCampaignIdFromQuery ?? location.state?.previousValues?.tradingAccount?.id
        }
        ibValidation={ibValidation}
        hasTMTOrTVAccount={hasTMTOrTVAccount}
      />
    </Loading>
  )
}

const transformCampaigns = (campaigns: ClientCampaignDto[], t: TFunction) => {
  return campaigns.reduce<CampaignExtendedNameDto[] | CampaignExtendedName[]>(
    (previousValue: CampaignExtendedNameDto[] | CampaignExtendedName[], currentValue) => {
      return [
        ...previousValue,
        {
          id: currentValue.id,
          name: currentValue.name,
          isCampaign: true,
          termsAndConditions: currentValue.termsAndConditionsUrl || '',
          type: currentValue.type,
          platformTypes: currentValue.platformTypes,
          calculationType: currentValue.calculationType,
          campaignDescription: currentValue.campaignDescription,
        },
      ]
    },
    [
      {
        id: '0',
        name: t('Trading Account.Regular Trading Account'),
        platformTypes: [],
      },
    ]
  )
}
